import type { TOC } from '@ember/component/template-only';
import { PhCheck } from 'ember-phosphor-icons';

interface ConfirmationCheckIconSignature {
  Element: HTMLDivElement;
}

/**
 * Animated purple check-circle that opens a "confirmation notification" —
 * application submitted, CourseCa$h™ used, CourseCa$h™ receipt provided,
 * evidence submitted, additional document provided, etc. Same icon across
 * every one of those moments; only the surrounding header/body copy differs
 * per call site (Figma: Confirmation Specs).
 *
 * Bounces in once on mount, from a dim 8px dot to the full purple-500 circle
 * + glow (`.tio-confirmation-check-*` in animations.css). Colors are pinned
 * to the raw purple-500/white atoms rather than the theme-adaptive
 * `--brand`/`--text-on-brand` tokens — the spec calls for this to always
 * read as solid purple-500 with a white check, mirroring the (also
 * non-adaptive) progress dots, regardless of light/dark mode.
 *
 * @example
 * ```gts
 * import ConfirmationCheckIcon from 'tio-ui/components/confirmation-check-icon';
 *
 * <ConfirmationCheckIcon />
 * ```
 */
const ConfirmationCheckIcon: TOC<ConfirmationCheckIconSignature> = <template>
  <div
    class="tio-confirmation-check"
    data-test-confirmation-check-icon
    aria-hidden="true"
    ...attributes
  >
    <span class="tio-confirmation-check-glow"></span>
    <span class="tio-confirmation-check-circle">
      <PhCheck @size={{16}} @weight="bold" class="tio-confirmation-check-icon" />
    </span>
  </div>
</template>;

export default ConfirmationCheckIcon;
